import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { AppShell } from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet } from "lucide-react";
import { toDMY } from "@/lib/dates";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

const n = (v) => (v === null || v === undefined || v === "" ? "-" : v);

export default function ItemLedger() {
  const [profile, setProfile] = useState(null);
  const [fabrics, setFabrics] = useState([]);
  const [label, setLabel] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/fabrics").then(({ data }) => setFabrics(data)).catch(() => {});
  }, []);

  const fabricLabel = (f) => `${f.quality_name}${f.shade_number ? ` · shade ${f.shade_number}` : ""}`;
  const byLabel = useMemo(() => Object.fromEntries(fabrics.map((f) => [fabricLabel(f), f])), [fabrics]);
  const fabric = byLabel[label];

  const load = async () => {
    if (!fabric) {
      toast.error("Pick an item from the list");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/ledger", {
        params: { fabric_id: fabric.id, date_from: from, date_to: to },
      });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const download = async (format) => {
    if (!fabric) return toast.error("Pick an item first");
    try {
      const res = await api.get("/ledger/export", {
        params: { fabric_id: fabric.id, date_from: from, date_to: to, format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `item-ledger.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Display</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Item Ledger</h1>
        </div>

        <div className="grid grid-cols-1 gap-4 py-6 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
          <div className="lg:col-span-2">
            <L>Item (quality · shade)</L>
            <Input
              data-testid="ledger-item-input"
              list="ledger-item-options"
              className="mt-2 rounded-sm"
              placeholder="Type quality · shade"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <datalist id="ledger-item-options">
              {fabrics.map((f, i) => (
                <option key={`${f.id}-${i}`} value={fabricLabel(f)} />
              ))}
            </datalist>
          </div>
          <div>
            <L>From</L>
            <DateInput data-testid="ledger-from-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={from} onChange={(v) => setFrom(v)} />
          </div>
          <div>
            <L>To</L>
            <DateInput data-testid="ledger-to-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={to} onChange={(v) => setTo(v)} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="ledger-run-button" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" onClick={load}>
              View ledger
            </Button>
            <Button data-testid="ledger-pdf-button" variant="outline" className="rounded-sm" onClick={() => download("pdf")}>
              <Download size={15} className="mr-2" /> PDF
            </Button>
            <Button data-testid="ledger-excel-button" variant="outline" className="rounded-sm" onClick={() => download("excel")}>
              <FileSpreadsheet size={15} className="mr-2" /> Excel
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : !data ? (
          <div data-testid="ledger-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">Pick an item</h3>
            <p className="mt-1 text-sm text-zinc-500">Every purchase, sale and adjustment of that shade will appear here.</p>
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-4 border border-zinc-200 bg-white p-4">
              <div>
                <p className="font-head text-sm font-bold uppercase tracking-tight">
                  {data.item.quality_name}
                  {data.item.shade_number ? ` · shade ${data.item.shade_number}` : ""}
                </p>
                <p className="text-xs text-zinc-500">{data.item.company_name || "-"}</p>
              </div>
              <div className="flex gap-5" data-testid="ledger-closing">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Closing than</p>
                  <p className="font-mono-num text-xl font-semibold">{data.closing.than}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Closing meter</p>
                  <p className="font-mono-num text-xl font-semibold">{data.closing.meter}</p>
                </div>
              </div>
            </div>

            {data.entries.length === 0 ? (
              <div className="border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">
                No transactions for this item in the selected period.
              </div>
            ) : (
              <div className="overflow-x-auto border border-zinc-200">
                <table className="w-full min-w-[900px] text-left text-sm" data-testid="ledger-table">
                  <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                    <tr>
                      {["Date", "Type", "Reference", "Party", "In than", "In mtr", "Out than", "Out mtr", "Rate", "Amount", "Bal than", "Bal mtr"].map((h) => (
                        <th key={h} className="px-3 py-2 font-bold">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200">
                    {data.entries.map((e, i) => (
                      <tr key={`${e.id}-${i}`} data-testid={`ledger-row-${i}`}>
                        <td className="px-3 py-2 font-mono-num">{toDMY(e.date)}</td>
                        <td className="px-3 py-2">{e.type}</td>
                        <td className="px-3 py-2 text-zinc-600">{e.ref_no}</td>
                        <td className="px-3 py-2 text-zinc-600">
                          {e.party || "-"}
                          {e.bale_no ? <span className="font-mono-num block text-[11px] text-zinc-400">Bale {e.bale_no}</span> : null}
                          {e.lr_no ? <span className="font-mono-num block text-[11px] text-zinc-400">LR {e.lr_no}</span> : null}
                        </td>
                        <td className="px-3 py-2 font-mono-num text-emerald-700">{e.in_than || "-"}</td>
                        <td className="px-3 py-2 font-mono-num text-emerald-700">{e.in_meter || "-"}</td>
                        <td className="px-3 py-2 font-mono-num text-red-600">{e.out_than || "-"}</td>
                        <td className="px-3 py-2 font-mono-num text-red-600">{e.out_meter || "-"}</td>
                        <td className="px-3 py-2 font-mono-num">{n(e.rate)}</td>
                        <td className="px-3 py-2 font-mono-num">{n(e.amount)}</td>
                        <td className="px-3 py-2 font-mono-num font-semibold">{e.balance_than}</td>
                        <td className="px-3 py-2 font-mono-num font-semibold">{e.balance_meter}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </AppShell>
  );
}
